import React, { useEffect, useMemo, useRef, useState } from "react"

import { createSimulationWrapperInstance } from "@/test-set/simulation-wrappers"

import { OBJECT_VIEW_MODE } from "@/lib/viewModes"
import type { JsonObject } from "@/types/json"

import { useTestSetEditorContext } from "../../contexts/TestSetEditorContext"
import { EVENTS, useEventBus, useEventDispatcher } from "../../event-bus"
import type { EditableTestCase } from "../../model"
import {
  getPendingEditForTestCase,
  isPendingDelete,
} from "../../utils/pendingChangesValidation"
import { CollapsedDataCell } from "./cells/CollapsedDataCell"
import { TestCaseBehaviorsCell } from "./cells/TestCaseBehaviorsCell"
import { TestCaseDescriptionCell } from "./cells/TestCaseDescriptionCell"
import { TestCaseIdCell } from "./cells/TestCaseIdCell"
import { TestCaseInputCell } from "./cells/TestCaseInputCell"
import { TestCaseKbEntriesCell } from "./cells/TestCaseKbEntriesCell"
import { TestCaseMetadataCell } from "./cells/TestCaseMetadataCell"
import { TestCaseNumberCell } from "./cells/TestCaseNumberCell"
import { TestCaseTypeCell } from "./cells/TestCaseTypeCell"
import { useGridNav } from "./GridNav"
import { MetadataColumnCells } from "./MetadataColumnCells"
import type { ColumnWidths } from "./useResizableColumns"

const COLLAPSED_DATA_WIDTH = "480px"

interface TestCaseRowProps {
  testCase: EditableTestCase
  /** Position within the visible (filtered) list; drives range selection. */
  index: number
  /** Position within the full list; drives the displayed row number. */
  displayIndex: number
  columnWidths: ColumnWidths
  isMultiSelected: boolean
}

export const TestCaseRow: React.FC<TestCaseRowProps> = ({
  testCase,
  index,
  displayIndex,
  columnWidths,
  isMultiSelected,
}) => {
  const dispatch = useEventDispatcher()
  const {
    selectedTestCaseId,
    expectedBehaviorTestCaseId,
    requestExpectedBehaviorTest,
    pendingChanges,
    visibleColumns,
    metadata,
    metadataViewMode,
  } = useTestSetEditorContext()
  const rowRef = useRef<HTMLTableRowElement>(null)
  const gridNav = useGridNav()
  const didFocusInputRef = useRef(false)
  const selectedByFocusRef = useRef(false)

  const [isExpanded, setIsExpanded] = useState(false)

  const isSelected = selectedTestCaseId === testCase.id
  const isExpectedBehaviorTestActive =
    expectedBehaviorTestCaseId === testCase.id
  const pendingEdit = getPendingEditForTestCase(testCase.id, pendingChanges)
  const hasPendingEdit = pendingEdit !== null
  const isMarkedForDeletion = isPendingDelete(testCase.id, pendingChanges)
  const hasPendingState = isMarkedForDeletion || hasPendingEdit

  // Auto-expand for empty test cases
  useEffect(() => {
    if (
      isSelected &&
      !testCase.input_str &&
      testCase.expected_behaviors.length === 0
    ) {
      setIsExpanded(true)
    }
  }, [isSelected, testCase.input_str, testCase.expected_behaviors])

  // Auto-expand when there's a pending edit (for copilot suggestions)
  useEffect(() => {
    if (hasPendingEdit) {
      setIsExpanded(true)
    }
  }, [hasPendingEdit])

  // Listen for expand all event
  useEventBus(EVENTS.EXPAND_ALL, () => {
    setIsExpanded(true)
  })

  // Listen for collapse all event
  useEventBus(EVENTS.COLLAPSE_ALL, () => {
    setIsExpanded(false)
  })

  // Scroll to selected test case
  useEffect(() => {
    if (!isSelected) {
      return
    }
    // Keyboard navigation already scrolled the focused cell into view.
    if (selectedByFocusRef.current) {
      selectedByFocusRef.current = false
      return
    }
    if (rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [isSelected])

  // Focus the simulation input of a freshly added (still empty, unsaved) row so
  // the user can start typing immediately after "Add Test Case". Runs once.
  useEffect(() => {
    if (
      didFocusInputRef.current ||
      !isSelected ||
      !testCase.id.startsWith("new-") ||
      testCase.input_str ||
      testCase.expected_behaviors.length > 0
    ) {
      return
    }
    didFocusInputRef.current = true
    const inputCell = rowRef.current?.querySelector<HTMLElement>(
      '[data-cell-role="simulation-input"]',
    )
    if (inputCell) {
      // Open straight into edit mode on focus (see useGridCell auto-edit handoff).
      gridNav.requestAutoEdit()
      inputCell.focus()
    }
  }, [
    isSelected,
    testCase.id,
    testCase.input_str,
    testCase.expected_behaviors,
    gridNav,
  ])

  const handleRowClick = () => {
    if (expectedBehaviorTestCaseId && !testCase.id.startsWith("new-")) {
      requestExpectedBehaviorTest(testCase.id)
      return
    }
    dispatch(EVENTS.TEST_CASE_SELECT, { testCaseId: testCase.id })
  }

  // Keyboard focus entering the row selects it, as a click does. Enter and
  // Space stay with the focused cell, which owns its own activation.
  const handleRowFocus = (e: React.FocusEvent<HTMLTableRowElement>) => {
    if (
      isSelected ||
      e.currentTarget.contains(e.relatedTarget as Node | null) ||
      !(e.target as HTMLElement).matches(":focus-visible")
    ) {
      return
    }
    selectedByFocusRef.current = true
    dispatch(EVENTS.TEST_CASE_SELECT, { testCaseId: testCase.id })
  }

  const handleToggleSelect = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    if (hasPendingState) {
      return
    }
    dispatch(EVENTS.TEST_CASE_TOGGLE_SELECT, {
      testCaseId: testCase.id,
      index,
      shiftKey: e.shiftKey,
    })
  }

  const simulationWrapper = useMemo(() => {
    if (!metadata.simulationWrapperType) {
      return null
    }
    return createSimulationWrapperInstance(metadata.simulationWrapperType)
  }, [metadata.simulationWrapperType])

  const handleUpdate = (updates: Partial<EditableTestCase>) => {
    dispatch(EVENTS.TEST_CASE_UPDATE, { testCaseId: testCase.id, updates })
  }

  const handleSimulationMetadataUpdate = (updates: JsonObject) => {
    dispatch(EVENTS.TEST_CASE_UPDATE, {
      testCaseId: testCase.id,
      updates: { simulation_metadata_input: updates },
    })
  }

  const getRowClassName = () => {
    const baseClass =
      "group border-b border-coval-border cursor-pointer transition-colors bg-surface-default"
    if (isMarkedForDeletion) {
      return `${baseClass} bg-red-500/20 dark:bg-red-500/30`
    }
    if (isExpectedBehaviorTestActive) {
      return `${baseClass} bg-surface-2 ring-1 ring-inset ring-coval-border-active`
    }
    if (isMultiSelected) {
      return `${baseClass} bg-surface-2`
    }
    return `${baseClass} hover:bg-surface-0`
  }

  const handleCaptureClick = () => {
    if (hasPendingState) {
      dispatch(EVENTS.EXPAND_PENDING_CHANGES, {})
    }
  }

  return (
    <tr
      ref={rowRef}
      className={getRowClassName()}
      onClick={handleRowClick}
      onClickCapture={handleCaptureClick}
      onFocus={handleRowFocus}
      onKeyDownCapture={(e) => {
        if (hasPendingState && e.key === "Enter") {
          e.stopPropagation()
          e.preventDefault()
          dispatch(EVENTS.EXPAND_PENDING_CHANGES, {})
        }
      }}
      data-test-case-id={testCase.id}
      data-covi-highlight-id={testCase.id}
      data-testid={`test-set-editor-row-${testCase.id}`}
      aria-current={isExpectedBehaviorTestActive ? "true" : undefined}
    >
      <TestCaseNumberCell
        index={index}
        displayIndex={displayIndex}
        isMarkedForDeletion={isMarkedForDeletion}
        isMultiSelected={isMultiSelected}
        onToggleSelect={handleToggleSelect}
        width={columnWidths.number}
      />

      {visibleColumns.has("id") && (
        <TestCaseIdCell id={testCase.id} width={columnWidths.id} />
      )}

      {visibleColumns.has("type") && (
        <TestCaseTypeCell
          testCase={testCase}
          onUpdate={handleUpdate}
          width={columnWidths.type}
        />
      )}

      <TestCaseInputCell
        testCase={testCase}
        pendingEdit={pendingEdit}
        isExpanded={isExpanded}
        isMarkedForDeletion={isMarkedForDeletion}
        onUpdate={handleUpdate}
        width={columnWidths.description}
      />

      {visibleColumns.has("description") && (
        <TestCaseDescriptionCell
          testCase={testCase}
          isExpanded={isExpanded}
          onUpdate={handleUpdate}
          width={columnWidths.testCaseDescription}
          isMarkedForDeletion={isMarkedForDeletion}
        />
      )}

      {metadataViewMode === OBJECT_VIEW_MODE.JSON &&
      (visibleColumns.has("behaviors") ||
        visibleColumns.has("metadata") ||
        visibleColumns.has("kbEntries")) ? (
        <CollapsedDataCell
          testCase={testCase}
          isExpanded={isExpanded}
          width={COLLAPSED_DATA_WIDTH}
          isMarkedForDeletion={isMarkedForDeletion}
        />
      ) : null}

      {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
        visibleColumns.has("behaviors") && (
          <TestCaseBehaviorsCell
            testCase={testCase}
            isExpanded={isExpanded}
            width={columnWidths.behaviors}
            isMarkedForDeletion={isMarkedForDeletion}
          />
        )}

      {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
        visibleColumns.has("metadata") &&
        (metadataViewMode === OBJECT_VIEW_MODE.COLUMNS ? (
          <MetadataColumnCells
            testCase={testCase}
            isExpanded={isExpanded}
            isMarkedForDeletion={isMarkedForDeletion}
          />
        ) : (
          <TestCaseMetadataCell
            testCase={testCase}
            isExpanded={isExpanded}
            width={columnWidths.metadata}
            isMarkedForDeletion={isMarkedForDeletion}
          />
        ))}

      {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
        visibleColumns.has("kbEntries") && (
          <TestCaseKbEntriesCell
            testCase={testCase}
            isExpanded={isExpanded}
            width={columnWidths.kbEntries}
          />
        )}

      {simulationWrapper?.renderTestCaseCell(
        testCase.simulation_metadata_input || null,
        handleSimulationMetadataUpdate,
        isExpanded,
        columnWidths.simulationMetadata,
      )}

      {/* Empty cell for "+ Add Column" header */}
      {columnWidths.addColumn !== "0px" && (
        <td className="p-0" style={{ width: columnWidths.addColumn }} />
      )}
    </tr>
  )
}
